import { Box, Paper, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { formatInteger, formatPercent } from "../formatters";
import { DonutSegment } from "./usersApiMapping";

// Fixed drawing size (rather than a responsive-fill container) so the ring
// stays proportioned to the card instead of floating small inside a wide
// flex box - mirrors the codebase's established donut pattern, see
// FtdLoadedPieChart.tsx.
const CHART_SIZE = { width: 190, height: 190 };

const CenterNumber = styled("text")({
  fill: "#1a1a1a",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 22,
  fontWeight: 700,
});

const CenterLabel = styled("text")({
  fill: "#757575",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 12,
  fontWeight: 500,
});

function PieCenterLabel({ value, label }: { value: number; label: string }) {
  const { width, height, left, top } = useDrawingArea();
  const centerX = left + width / 2;
  const centerY = top + height / 2;

  return (
    <>
      <CenterNumber x={centerX} y={centerY - 10}>
        {formatInteger(value)}
      </CenterNumber>
      <CenterLabel x={centerX} y={centerY + 12}>
        {label}
      </CenterLabel>
    </>
  );
}

// Segments with 0 users are dropped entirely (not rendered as a "0.0%" row)
// - the mockup's own dummy data does the same, e.g. "Qualified by zip code"
// only ever defines a YES segment, no NO placeholder.
function DonutLegend({ segments, total }: { segments: DonutSegment[]; total: number }) {
  return (
    <Stack spacing={1.25}>
      {segments.map((segment) => (
        <Stack key={segment.label} direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: segment.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 13, color: "#424242", minWidth: 32 }}>{segment.label}</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#424242" }}>
            {formatPercent(total ? (segment.value / total) * 100 : 0, 1)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

interface UsersDonutChartProps {
  title: string;
  segments: DonutSegment[];
  centerValue: number;
  centerLabel: string;
}

export default function UsersDonutChart({
  title,
  segments,
  centerValue,
  centerLabel,
}: UsersDonutChartProps) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const visibleSegments = segments.filter((segment) => segment.value > 0);

  return (
    <Paper variant="outlined" sx={{ p: "18px 20px", borderColor: "#ececec" }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={5}>
        <PieChart
          {...CHART_SIZE}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          series={[
            {
              data: visibleSegments.map((segment, index) => ({
                id: index,
                value: segment.value,
                label: segment.label,
                color: segment.color,
              })),
              innerRadius: 55,
              outerRadius: 85,
            },
          ]}
          slotProps={{ legend: { hidden: true } }}
        >
          <PieCenterLabel value={centerValue} label={centerLabel} />
        </PieChart>
        <DonutLegend segments={visibleSegments} total={total} />
      </Stack>
    </Paper>
  );
}
